import React from "react"
import Alert from "react-bootstrap/Alert"
import Form from "react-bootstrap/Form"
import Modal from "react-bootstrap/Modal"
import Spinner from "react-bootstrap/Spinner"
import { CreditCard, MapPin, User } from "lucide-react"
import Button from "../common/Button.jsx"
import InputField from "../order-form/InputField.jsx"
import { useCountries } from "../order-form/useCountries.js"
import { useRegionOptions } from "../order-form/useRegionOptions.js"

function formatErrorMessage(error) {
    if (!error) return "Unknown error"
    if (typeof error === "string") return error
    if (error instanceof Error) return error.message || "Unknown error"
    try {
        return JSON.stringify(error)
    } catch {
        return String(error)
    }
}

function emptyAddress() {
    return {
        first_name: "",
        last_name: "",
        company: "",
        email: "",
        street_address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        phone: "",
    }
}

function addressFromApi(a) {
    if (!a) return emptyAddress()
    return {
        id: a.id ?? null,
        first_name: a.first_name ?? "",
        last_name: a.last_name ?? "",
        company: a.company ?? "",
        email: a.email ?? "",
        street_address: a.street_address ?? "",
        city: a.city ?? "",
        state: a.state ?? "",
        zip: a.zip ?? "",
        country: a.country ?? "",
        phone: a.phone ?? "",
    }
}

function customerFormFromApi(c) {
    return {
        id: c?.id ?? null,
        first_name: c?.first_name ?? "",
        last_name: c?.last_name ?? "",
        email: c?.email ?? "",
        phone: c?.phone ?? "",
        company: c?.company ?? "",
        contact_method: c?.contact_method ?? "email",
        is_staff: Boolean(c?.is_staff),
        fishing_license: c?.fishing_license ?? "",
        trip_code: c?.trip_code ?? "",
        fish_tag: c?.fish_tag ?? "",
        bill_same_as_ship: c == null ? true : Boolean(c?.bill_same_as_ship),
        default_bill_id: c?.default_bill_id ?? null,
        default_ship_id: c?.default_ship_id ?? null,
        ship_to: addressFromApi(c?.ship_to),
        bill_to: addressFromApi(c?.bill_to),
    }
}

function hasAnyAddressField(a) {
    if (!a) return false
    const keys = [
        "first_name",
        "last_name",
        "company",
        "email",
        "street_address",
        "city",
        "state",
        "zip",
        "country",
        "phone",
    ]
    return keys.some((k) => String(a[k] ?? "").trim() !== "")
}

/**
 * @param {{
 *   show: boolean,
 *   mode: "create" | "edit",
 *   customer: any | null,
 *   api: any,
 *   onClose: () => void,
 *   onSaved: (customer: any) => void,
 * }} props
 */
export default function CreateEditCustomerModal({
    show,
    mode,
    customer,
    api,
    onClose,
    onSaved,
}) {
    const [form, setForm] = React.useState(() => customerFormFromApi(customer))
    const [isLoading, setIsLoading] = React.useState(false)
    const [error, setError] = React.useState("")
    const { countries, countryNames } = useCountries()
    const shipRegionOptions = useRegionOptions(String(form.ship_to?.country ?? ""), countries)
    const billRegionOptions = useRegionOptions(String(form.bill_to?.country ?? ""), countries)

    React.useEffect(() => {
        if (!show) return
        setError("")
        setIsLoading(false)
        setForm(customerFormFromApi(customer))

        // Best-effort: if editing and api.getCustomer exists, hydrate full shape.
        if (mode !== "edit") return
        const id = customer?.id ?? null
        if (!id) return
        if (typeof api?.getCustomer !== "function") return

        let cancelled = false
        setIsLoading(true)
        api.getCustomer(id)
            .then((full) => {
                if (cancelled) return
                // support both {data} and raw customer shapes
                const resolved = full?.data ?? full
                setForm(customerFormFromApi(resolved))
            })
            .catch(() => {
                // non-blocking; keep existing customer snapshot
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [show, mode, customer?.id])

    function setField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }))
    }

    function setShipField(key, value) {
        setForm((prev) => ({ ...prev, ship_to: { ...(prev.ship_to ?? {}), [key]: value } }))
    }

    function setBillField(key, value) {
        setForm((prev) => ({ ...prev, bill_to: { ...(prev.bill_to ?? {}), [key]: value } }))
    }

    async function ensureAddress(customerId, addr, preferredIdKey) {
        if (!hasAnyAddressField(addr)) {
            return null
        }
        const addressId = addr?.id ?? null
        if (addressId && typeof api?.updateCustomerAddress === "function") {
            const updated = await api.updateCustomerAddress(customerId, addressId, addr)
            return updated?.id ?? addressId
        }
        if (typeof api?.createCustomerAddress === "function") {
            const created = await api.createCustomerAddress(customerId, addr)
            return created?.id ?? null
        }
        // If we can’t create/update addresses, just skip (still allow saving contact info).
        return null
    }

    async function handleSave() {
        setError("")
        setIsLoading(true)
        try {
            const payload = {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                contact_method: form.contact_method,
                is_staff: Boolean(form.is_staff),
                fishing_license: form.fishing_license,
                trip_code: form.trip_code,
                fish_tag: form.fish_tag,
                bill_same_as_ship: Boolean(form.bill_same_as_ship),
            }

            let savedCustomer
            if (mode === "create") {
                if (typeof api?.createCustomer !== "function") {
                    throw new Error("api.createCustomer is not available")
                }
                const res = await api.createCustomer(payload)
                savedCustomer = res?.data ?? res
            } else {
                const id = form.id ?? customer?.id ?? null
                if (!id) {
                    throw new Error("Missing customer id")
                }
                if (typeof api?.updateCustomer !== "function") {
                    throw new Error("api.updateCustomer is not available")
                }
                const res = await api.updateCustomer(id, { ...payload, id })
                savedCustomer = res?.data ?? res
            }

            const customerId = savedCustomer?.id ?? form.id ?? customer?.id
            if (!customerId) {
                onSaved(savedCustomer)
                onClose()
                return
            }

            // Addresses: optionally create/update and then persist default ids.
            const shipAddr = form.ship_to
            const shipId = await ensureAddress(customerId, shipAddr, "default_ship_id")

            const billId = form.bill_same_as_ship
                ? shipId
                : await ensureAddress(customerId, form.bill_to, "default_bill_id")

            if ((shipId || billId) && typeof api?.updateCustomer === "function") {
                const res2 = await api.updateCustomer(customerId, {
                    ...payload,
                    id: customerId,
                    default_ship_id: shipId ?? savedCustomer?.default_ship_id ?? null,
                    default_bill_id: billId ?? savedCustomer?.default_bill_id ?? null,
                    bill_same_as_ship: Boolean(form.bill_same_as_ship),
                })
                savedCustomer = res2?.data ?? res2
            }

            onSaved(savedCustomer)
            onClose()
        } catch (e) {
            setError(formatErrorMessage(e))
        } finally {
            setIsLoading(false)
        }
    }

    const title = mode === "edit" ? "Edit Customer" : "New Customer"

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
                {error ? (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                ) : null}

                <div className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <User size={18} />
                        <div className="fw-semibold">Contact</div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <InputField
                                label="First Name"
                                value={form.first_name}
                                onChange={(e) => setField("first_name", e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <InputField
                                label="Last Name"
                                value={form.last_name}
                                onChange={(e) => setField("last_name", e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <InputField
                                label="Email"
                                value={form.email}
                                onChange={(e) => setField("email", e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <InputField
                                label="Phone"
                                value={form.phone}
                                onChange={(e) => setField("phone", e.target.value)}
                            />
                        </div>
                    </div>
                    <InputField
                        label="Company"
                        value={form.company}
                        onChange={(e) => setField("company", e.target.value)}
                    />

                    <div className="row">
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="form-label fw-semibold" style={{ fontSize: "0.875rem" }}>
                                    Contact Method
                                </label>
                                <Form.Select
                                    value={form.contact_method}
                                    onChange={(e) => setField("contact_method", e.currentTarget.value)}
                                >
                                    <option value="phone">Phone</option>
                                    <option value="email">Email</option>
                                    <option value="text">Text</option>
                                </Form.Select>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                {/* Invisible label to align checkbox with the select control (not the label). */}
                                <label
                                    className="form-label fw-semibold invisible"
                                    style={{ fontSize: "0.875rem" }}
                                >
                                    Contact Method
                                </label>
                                <div className="d-flex align-items-center" style={{ height: "38px" }}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Staff"
                                        checked={Boolean(form.is_staff)}
                                        onChange={(e) => setField("is_staff", e.currentTarget.checked)}
                                        className="mb-0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <InputField
                                label="Fishing License"
                                value={form.fishing_license}
                                onChange={(e) => setField("fishing_license", e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <InputField
                                label="Trip Code"
                                value={form.trip_code}
                                onChange={(e) => setField("trip_code", e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <InputField
                                label="Fish Tag"
                                value={form.fish_tag}
                                onChange={(e) => setField("fish_tag", e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <hr className="my-4" style={{ borderColor: "#e9ecef", opacity: 1 }} />

                <div className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <MapPin size={18} />
                        <div className="fw-semibold">Shipping</div>
                    </div>
                    <div className="row">
                        <div className="col-12 mb-3">
                            <label className="form-label fw-semibold" style={{ fontSize: "0.875rem" }}>
                                Country
                            </label>
                            <select
                                className="form-select"
                                value={String(form.ship_to?.country ?? "")}
                                onChange={(e) => {
                                    const nextCountry = e.currentTarget.value
                                    setShipField("country", nextCountry)
                                    // clear state when changing country (mirrors order form behavior)
                                    setShipField("state", "")
                                }}
                            >
                                <option value="">Select a country</option>
                                {countryNames.map((country) => (
                                    <option key={country} value={country}>{country}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <InputField
                                label="First Name"
                                value={form.ship_to?.first_name ?? ""}
                                onChange={(e) => setShipField("first_name", e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <InputField
                                label="Last Name"
                                value={form.ship_to?.last_name ?? ""}
                                onChange={(e) => setShipField("last_name", e.target.value)}
                            />
                        </div>
                    </div>
                    <InputField
                        label="Company"
                        value={form.ship_to?.company ?? ""}
                        onChange={(e) => setShipField("company", e.target.value)}
                    />
                    <InputField
                        label="Street Address"
                        value={form.ship_to?.street_address ?? ""}
                        onChange={(e) => setShipField("street_address", e.target.value)}
                    />
                    <div className="row">
                        <div className="col-md-5">
                            <InputField
                                label="City"
                                value={form.ship_to?.city ?? ""}
                                onChange={(e) => setShipField("city", e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            {shipRegionOptions.length ? (
                                <div className="mb-3">
                                    <label className="form-label fw-semibold" style={{ fontSize: "0.875rem" }}>
                                        State
                                    </label>
                                    <select
                                        className="form-select"
                                        value={String(form.ship_to?.state ?? "")}
                                        onChange={(e) => setShipField("state", e.currentTarget.value)}
                                    >
                                        <option value="">Select a state</option>
                                        {shipRegionOptions.map((s) => (
                                            <option key={s.state_code} value={s.state_code}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <InputField
                                    label="State"
                                    value={form.ship_to?.state ?? ""}
                                    onChange={(e) => setShipField("state", e.target.value)}
                                />
                            )}
                        </div>
                        <div className="col-md-4">
                            <InputField
                                label="ZIP / Postal"
                                value={form.ship_to?.zip ?? ""}
                                onChange={(e) => setShipField("zip", e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <InputField
                                label="Phone"
                                value={form.ship_to?.phone ?? ""}
                                onChange={(e) => setShipField("phone", e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <InputField
                                label="Email"
                                value={form.ship_to?.email ?? ""}
                                onChange={(e) => setShipField("email", e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <hr className="my-4" style={{ borderColor: "#e9ecef", opacity: 1 }} />

                <div className="mb-2">
                    <div className="d-flex align-items-center gap-2 mb-2">
                        <CreditCard size={18} />
                        <div className="fw-semibold">Billing</div>
                    </div>
                    <Form.Check
                        type="checkbox"
                        label="Same as shipping"
                        checked={Boolean(form.bill_same_as_ship)}
                        onChange={(e) => setField("bill_same_as_ship", e.currentTarget.checked)}
                        className="mb-3"
                    />
                    {!form.bill_same_as_ship ? (
                        <>
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-semibold" style={{ fontSize: "0.875rem" }}>
                                        Country
                                    </label>
                                    <select
                                        className="form-select"
                                        value={String(form.bill_to?.country ?? "")}
                                        onChange={(e) => {
                                            const nextCountry = e.currentTarget.value
                                            setBillField("country", nextCountry)
                                            setBillField("state", "")
                                        }}
                                    >
                                        <option value="">Select a country</option>
                                        {countryNames.map((country) => (
                                            <option key={country} value={country}>{country}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <InputField
                                        label="First Name"
                                        value={form.bill_to?.first_name ?? ""}
                                        onChange={(e) => setBillField("first_name", e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <InputField
                                        label="Last Name"
                                        value={form.bill_to?.last_name ?? ""}
                                        onChange={(e) => setBillField("last_name", e.target.value)}
                                    />
                                </div>
                            </div>
                            <InputField
                                label="Company"
                                value={form.bill_to?.company ?? ""}
                                onChange={(e) => setBillField("company", e.target.value)}
                            />
                            <InputField
                                label="Street Address"
                                value={form.bill_to?.street_address ?? ""}
                                onChange={(e) => setBillField("street_address", e.target.value)}
                            />
                            <div className="row">
                                <div className="col-md-5">
                                    <InputField
                                        label="City"
                                        value={form.bill_to?.city ?? ""}
                                        onChange={(e) => setBillField("city", e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    {billRegionOptions.length ? (
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold" style={{ fontSize: "0.875rem" }}>
                                                State
                                            </label>
                                            <select
                                                className="form-select"
                                                value={String(form.bill_to?.state ?? "")}
                                                onChange={(e) => setBillField("state", e.currentTarget.value)}
                                            >
                                                <option value="">Select a state</option>
                                                {billRegionOptions.map((s) => (
                                                    <option key={s.state_code} value={s.state_code}>
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <InputField
                                            label="State"
                                            value={form.bill_to?.state ?? ""}
                                            onChange={(e) => setBillField("state", e.target.value)}
                                        />
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <InputField
                                        label="ZIP / Postal"
                                        value={form.bill_to?.zip ?? ""}
                                        onChange={(e) => setBillField("zip", e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6">
                                    <InputField
                                        label="Phone"
                                        value={form.bill_to?.phone ?? ""}
                                        onChange={(e) => setBillField("phone", e.target.value)}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <InputField
                                        label="Email"
                                        value={form.bill_to?.email ?? ""}
                                        onChange={(e) => setBillField("email", e.target.value)}
                                    />
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    {isLoading ? (
                        <>
                            <Spinner size="sm" />
                            <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                                Saving…
                            </span>
                        </>
                    ) : null}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button variant="custom" onClick={handleSave} disabled={isLoading}>
                        Save
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

